import { ApolloClient, InMemoryCache, HttpLink, ApolloLink } from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs';
import history from './History';
import { clearToken, getToken, isSignedIn, isTokenExpired, refreshToken, isSessionExpired } from './util/sessions';
import DebounceLink from './util/DebounceLink';
import { setSnackbarMessage } from './context/SnackbarMessage';
import { serverUrl } from './environment';
import { CombinedGraphQLErrors, CombinedProtocolErrors, ServerError } from '@apollo/client';

let SERVER_URL = serverUrl;
// SERVER_URL = 'http://hparsons.php72.local';
let introspectionQueryResultData = require('./fragmentTypesDev');

// Convert Apollo 2 fragment matcher format to Apollo 3 possibleTypes format
function buildPossibleTypes(introspectionData) {
    const possibleTypes = {};
    introspectionData.__schema.types.forEach(supertype => {
        if (supertype.possibleTypes) {
            possibleTypes[supertype.name] = supertype.possibleTypes.map(subtype => subtype.name);
        }
    });
    return possibleTypes;
}

const possibleTypes = buildPossibleTypes(introspectionQueryResultData);

export const GQL_ENDPOINT_PRIMARY = '/graphql';
export const GQL_ENDPOINT_UTILITIES = '/graphql';
export const GQL_ENDPOINT_AUTH_JWT = '/graphql';
export const GQL_ENDPOINT_ENQUIRIES = '/graphql';
export const GQL_ENDPOINT_TRANSFERS = '/graphql';
export const GQL_ENDPOINT_CALENDAR = '/graphql';
export const GQL_ENDPOINT_DEFAULT = '/graphql';
export const GQL_ENDPOINT_ADMIN = '/graphql';

let SERVICE_URL = SERVER_URL + GQL_ENDPOINT_PRIMARY;

let client;

export function getServerLink() {
    return SERVER_URL;
}

/**
 * This link is executed after a request fails.
 * We use this to determine if the request has failed due to authorisation.
 * If so we reset the client and redirect to the sign-in page.
 */
const handleError = ({ error }) => {
    const graphQLErrors = CombinedGraphQLErrors.is(error) ? error.errors : null;
    const protocolErrors = CombinedProtocolErrors.is(error) ? error.errors : null;
    let networkError = null;
    const errorList = [];
    if (!!graphQLErrors && graphQLErrors.length) graphQLErrors.forEach(err => errorList.push({ message: err.message }));
    if (!!protocolErrors && protocolErrors.length)
        protocolErrors.forEach(err => errorList.push({ message: err.message }));
    if (!errorList.length) {
        networkError = error;
        errorList.push({ message: networkError?.message || networkError || 'Unknown error' });
    }
    errorList.forEach(err => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'Snackbar Message',
            Message: err.message || null
        });
    });

    // A bit hacky, but whenever you get an error due to being unauthorised the
    // error message from the graphql server always starts with `Authentication required.`
    // TODO: allow user to log back in instead of ejecting immediately
    if (graphQLErrors && graphQLErrors.some(e => e.message?.startsWith('Authentication required.') || 401 === e.code)) {
        setSnackbarMessage('Unauthorised user was logged out.');
        clearToken();
        resetClient();
        return;
    }

    let graphQLErrorsString = '';
    if (graphQLErrors) {
        graphQLErrors.forEach(({ message, code, validation }) => {
            if (graphQLErrorsString !== '') {
                graphQLErrorsString += '\n \n';
            }
            if (418 === code) {
                graphQLErrorsString += `[Application error]: ${message}`;
            } else if (403 === code) {
                graphQLErrorsString += `[Permission error]: ${message}`;
            } else if (validation && !!validation.length) {
                graphQLErrorsString += `[Validation error]: ${message}`;
            } else {
                graphQLErrorsString += `[${!code || 200 === code ? 'Application' : code} Error]: ${message}`;
            }
        });
        // Omg snackbar from anywhere.
    }
    if (networkError) {
        let errorMsg = networkError.error?.message || networkError;
        if (ServerError.is?.(networkError)) {
            if (418 === networkError.statusCode) {
                // maintenance mode
                const { errors: errs } = JSON.parse(networkError.bodyText || {});
                errorMsg = errs[0].message;
                graphQLErrorsString += `[Server alert]: ${errorMsg}`;
            }
        } else if (error?.name === 'AbortError') {
            // User navigated away, whatever.
        } else if (error?.message === 'Failed to fetch') {
            // Server dead!
            graphQLErrorsString += `[Server error]: ${errorMsg}`;
        } else {
            // who knows?
            graphQLErrorsString += `[Server error]: ${errorMsg}`;
        }
    }
    setSnackbarMessage(graphQLErrorsString);
    console.error(graphQLErrorsString);
};

/**
 * This link will debounce graphql requests which have a `debounceKey`
 * in the context.
 */
function getDebounceLink() {
    return new DebounceLink(250);
}

/**
 * This link actually makes the HTTP request, after the request has been
 * processed by the other link middleware.
 */
function getHttpLink() {
    let URL = SERVICE_URL;
    /* TODO: remove this API profiling config in production ? */
    if (document.cookie.indexOf('XDEBUG_PROFILE') >= 0) {
        URL += '?XDEBUG_PROFILE=1';
    }
    return new HttpLink({
        uri: URL,
        fetch: refreshFetch
    });
}

/**
 * A custom fetch function which adds auth headers to the request and
 * will attempt to refresh the token before a request if it's found
 * to be expired.
 */

function refreshFetch(uri, options) {
    // kind of hacky to put this block of code here
    // bcoz it is taking advantage of refreshFetch() executing on every outgoing request
    if (isSessionExpired()) {
        clearToken();
        return;
    }

    // TODO: It'd be better if we could do this after a failed request with 401
    // instead of doing this preemptively before every outgoing request
    if (isTokenExpired() && JSON.parse(options.body).operationName !== 'RefreshToken') {
        // If the current stored token has expired and this is not the request to refresh
        // the token, then we execute the refresh request before the original request

        return refreshToken(getAuthClient()).then(success => {
            return success === false ? resetClient() : executeFetch(uri, options);
        });
    }

    return executeFetch(uri, options);
}

function executeFetch(uri, options) {
    const token = getToken();
    if (token && token.value) options.headers.authorization = `Bearer ${token.value}`;

    return fetch(uri, options);
}

export function resetClient() {
    // We need to redirect to the /sign-in page first to un-mount all components with active queries
    const { pathname, search } = history.location;
    history.push('/sign-in', pathname === '/sign-in' ? undefined : { referrer: { pathname, search } });
    client.clearStore().catch(console.error);
    authClient.clearStore().catch(console.error);
}

function dataIdFromObject(obj) {
    const typeName = obj.__typename.toLowerCase();
    let idVal;
    switch (typeName) {
        default:
            idVal = obj.id || obj.Id || obj.ID || obj.Key;
    }
    if (!idVal) {
        if (
            !typeName.endsWith('connection') &&
            !typeName.endsWith('connection1') &&
            !typeName.endsWith('join') &&
            !typeName.endsWith('edge') &&
            !typeName.endsWith('pageinfo') &&
            !typeName.endsWith('filterbyinfo') &&
            !typeName.endsWith('filterbyinfooptions') &&
            !typeName.endsWith('filterbyoption') &&
            !typeName.endsWith('__type') &&
            !typeName.endsWith('__inputvalue') &&
            !typeName.endsWith('__enumvalue') &&
            !typeName.endsWith('multivaluefield') &&
            !typeName.endsWith('multivaluefieldcomposite') &&
            !typeName.endsWith('_multivaluefieldlist') &&
            !typeName.endsWith('searchresult') &&
            !typeName.endsWith('metricsresult') &&
            !typeName.endsWith('mutation') &&
            0 !== Number(idVal)
        )
            console.warn('ID missing on object ' + obj.__typename, obj);
        return undefined;
    }

    // Mix the _join record into the key so multiple parents sharing an outer ID don't
    // collapse into one cache bucket. Apollo normalizes depth-first, so the nested record
    // may already be a {__ref} by the time we read it — check that first.
    if (obj._join) {
        for (const k in obj._join) {
            if (k === '__typename') continue;
            const v = obj._join[k];
            if (!v || typeof v !== 'object') continue;
            if (v.__ref) {
                idVal += ':' + v.__ref;
                break;
            }
            if (v.ID) {
                idVal += ':' + v.__typename + ':' + v.ID;
                break;
            }
        }
    }

    return `${obj.__typename}:${idVal}`;
}

export function getClient() {
    if (client) return client;

    client = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(getHttpLink()),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    window.__client = client;

    client.onResetStore(() => {
        //history.push('/');
    });

    return client;
}

/**
 * Client for JWT authentication GQL endpoint `/authenticate/graphql`.
 */
let authClient;

export function getAuthClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (authClient && isSignedIn()) return authClient;

    authClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_AUTH_JWT,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache()
    });

    return authClient;
}

/**
 * Client for JWT authentication GQL endpoint `/authenticate/graphql`.
 */
let utilitiesClient;

export function getUtilitiesClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (utilitiesClient && isSignedIn()) return utilitiesClient;

    utilitiesClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_UTILITIES,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    return utilitiesClient;
}

/**
 * Client for enquires GQL endpoint `em-graphql`.
 */
let enquiryClient;

export function getEnquiryClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (enquiryClient && isSignedIn()) return enquiryClient;

    enquiryClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_ENQUIRIES,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    return enquiryClient;
}

/**
 * Client for transfers GQL endpoint `tm-graphql`.
 */
let transfersClient;

export function getTransfersClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (transfersClient && isSignedIn()) return transfersClient;

    transfersClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_TRANSFERS,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    return transfersClient;
}

/**
 * Client for transfers GQL endpoint `tm-graphql`.
 */
let calendarClient;

export function getCalendarClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (calendarClient && isSignedIn()) return calendarClient;

    calendarClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_CALENDAR,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    return calendarClient;
}

/**
 * Client for generic things GQL endpoint `/graphql`.
 */
let myClient;

export function getMyClient() {
    // needed to reset data if not logged in, resetStore() doesn't work.
    if (myClient && isSignedIn()) return myClient;

    myClient = new ApolloClient({
        link: onError(handleError)
            .concat(getDebounceLink())
            .concat(
                new HttpLink({
                    uri: SERVER_URL + GQL_ENDPOINT_DEFAULT,
                    fetch: refreshFetch
                })
            ),
        cache: new InMemoryCache({ dataIdFromObject, possibleTypes })
    });

    return myClient;
}

/**
 * Client for Assets GQL endpoint `/admin/graphql`.
 */
let assetsClient;

export function getAssetsClient() {
    if (assetsClient) return assetsClient;

    const uploadLink = new UploadHttpLink({
        uri: SERVER_URL + GQL_ENDPOINT_ADMIN,
        fetch: refreshFetch
    });

    assetsClient = new ApolloClient({
        link: ApolloLink.from([onError(handleError), getDebounceLink(), uploadLink]),
        cache: new InMemoryCache()
    });

    return assetsClient;
}

export function getServiceURLHostname() {
    return extractHostname(SERVICE_URL);
}

function extractHostname(url) {
    let hostname;
    //find & remove protocol (http, ftp, etc.) and get hostname

    if (url.indexOf('//') > -1) {
        hostname = url.split('/')[2];
    } else {
        hostname = url.split('/')[0];
    }

    //find & remove port number
    hostname = hostname.split(':')[0];
    //find & remove "?"
    hostname = hostname.split('?')[0];

    return hostname;
}
